import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DIVISIONS, LANGUAGES } from '@/config/appConfig';
import type { CountryOption, Division, UpdateUserRequest } from '../types/admin.types';

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  userName: string;
  countries: CountryOption[];
  onSubmit: (data: UpdateUserRequest) => Promise<void>;
  onClose: () => void;
}

export const CompleteProfileModal = ({ isOpen, isLoading, userName, countries, onSubmit, onClose }: Props) => {
  const [countryId, setCountryId] = useState<number | ''>('');
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [language, setLanguage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggleDivision = (d: Division) =>
    setDivisions((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const data: UpdateUserRequest = {};
      if (countryId !== '') data.country_id = countryId;
      if (divisions.length > 0) data.division = divisions;
      if (language) data.language = language;
      await onSubmit(data);
      setCountryId('');
      setDivisions([]);
      setLanguage('');
      setError(null);
      onClose();
    } catch {
      setError('Failed to update profile. Please try again.');
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setCountryId('');
    setDivisions([]);
    setLanguage('');
    setError(null);
    onClose();
  };

  const isEmpty = countryId === '' && divisions.length === 0 && !language;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Complete profile</DialogTitle>
          {userName && <DialogDescription>for {userName}</DialogDescription>}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <p className="text-sm text-slate-500">
            These fields are optional — the user can also complete them after their first login.
          </p>

          {/* Country */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="profile-country" className="text-sm font-medium text-slate-700">
              Country
            </label>
            <select
              id="profile-country"
              value={countryId}
              onChange={(e) => setCountryId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400 bg-white"
            >
              <option value="">Select a country…</option>
              {countries.map((c) => (
                <option key={c.country_id} value={c.country_id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Division — multi-select checkboxes */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">Division</span>
            <div className="rounded-lg border border-slate-200 px-3 py-2.5 flex flex-wrap gap-x-4 gap-y-2">
              {DIVISIONS.map((d) => (
                <label key={d} className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={divisions.includes(d as Division)}
                    onChange={() => toggleDivision(d as Division)}
                    disabled={isLoading}
                    className="rounded"
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="profile-language" className="text-sm font-medium text-slate-700">
              Language
            </label>
            <select
              id="profile-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400 bg-white"
            >
              <option value="">Select a language…</option>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Skip
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isEmpty}
            >
              {isLoading ? 'Saving…' : 'Save profile'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
